/**
 * Phase PWA — Offline banner.
 *
 * Renders a thin amber strip when `navigator.onLine === false`. Designed
 * to sit at the top of any layout. Disappears as soon as connection
 * returns. Doesn't block interaction — managers may still use cached
 * pages, and inspection submissions will be queued automatically.
 */
import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

export default function OfflineBanner() {
    const [online, setOnline] = useState(
        typeof navigator !== "undefined" ? navigator.onLine : true,
    );
    useEffect(() => {
        const up = () => setOnline(true);
        const down = () => setOnline(false);
        window.addEventListener("online", up);
        window.addEventListener("offline", down);
        return () => {
            window.removeEventListener("online", up);
            window.removeEventListener("offline", down);
        };
    }, []);
    if (online) return null;
    return (
        <div
            data-testid="pwa-offline-banner"
            className="w-full bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200 px-3 py-1.5 flex items-center justify-center gap-2 text-xs font-medium"
            role="status"
            aria-live="polite"
        >
            <WifiOff size={13} aria-hidden="true" />
            You&apos;re offline. Cached pages still work; new submissions will sync when you reconnect.
        </div>
    );
}
